import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { PiCalendarBlank } from 'react-icons/pi'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { getEnergyModeNameMap } from '../../../services/energy-mode-config'
import { energyLevelTracker } from '../../../services/energyLevelTracking'

// Energy modes in order from lowest to highest
const ENERGY_MODES_ORDERED: EnergyMode[] = [
  EnergyMode.DRAINED,
  EnergyMode.STRUGGLING,
  EnergyMode.FUNCTIONAL,
  EnergyMode.ENERGIZED,
  EnergyMode.PEAK,
]

interface PostVacationCheckinProps {
  previousFeedbackMode?: EnergyMode | null
  vacationDates: string
  nextRecommendationDate: string
  onEnergySelect?: (mode: EnergyMode) => void
}

export const PostVacationCheckin: React.FC<PostVacationCheckinProps> = ({
  previousFeedbackMode = null,
  vacationDates,
  nextRecommendationDate,
  onEnergySelect,
}) => {
  const { formatMessage } = useIntl()
  const energyModeNames = getEnergyModeNameMap(formatMessage)

  const previousIndex = previousFeedbackMode
    ? ENERGY_MODES_ORDERED.indexOf(previousFeedbackMode)
    : null

  const [selectedIndex, setSelectedIndex] = useState<number | null>(previousIndex)

  const handleSelect = (index: number) => {
    setSelectedIndex(index)
    const selectedMode = ENERGY_MODES_ORDERED[index]

    energyLevelTracker.trackEmptyStateCheckin({
      checkinType: 'post_vacation',
      selectedEnergyMode: selectedMode,
    })

    if (onEnergySelect) {
      onEnergySelect(selectedMode)
    }
  }

  return (
    <div className="checkin-card checkin-card--post-vacation">
      <div className="checkin-header-row">
        <div className="checkin-header">
          <span className="checkin-emoji" role="img" aria-label="wave">👋</span>
          <h3 className="checkin-title">
            {formatMessage({ id: 'energyLevel.postVacation.title' })}
          </h3>
        </div>
        <div className="days-until-badge green">
          <span className="badge-dot" />
          {formatMessage({ id: 'energyLevel.postVacation.quickCheckin' })}
        </div>
      </div>
      <p className="checkin-description">
        {formatMessage(
          { id: 'energyLevel.postVacation.description' },
          { dates: <strong key="dates">{vacationDates}</strong> }
        )}
      </p>

      <div className="energy-selector">
        {ENERGY_MODES_ORDERED.map((mode, index) => {
          const isSelected = selectedIndex === index

          return (
            <button
              key={mode}
              type="button"
              onClick={() => handleSelect(index)}
              className={`energy-selector-button energy-${mode}${isSelected ? ' selected' : ''}`}
            >
              <div className="battery-icon">
                <div className="battery-tip" />
                <div className={`battery-fill fill-${mode}`} />
              </div>
              <span className="energy-label">
                {energyModeNames[mode]}
              </span>
            </button>
          )
        })}
      </div>

      <div className="checkin-notice">
        <PiCalendarBlank className="notice-icon" />
        <span>
          {formatMessage(
            { id: 'energyLevel.postVacation.recommendationsResume' },
            { date: <strong key="date">{nextRecommendationDate}</strong> }
          )}
        </span>
      </div>
    </div>
  )
}

export default PostVacationCheckin
